import { HttpError } from '@refinedev/core';
import axios from 'axios';

import { AuthErrors } from './constants/errorCodes';
import { API_URL, APP_BASENAME, AUTH_URLS } from './constants/urls';
import { captureHandledError, getHttpErrorPolicy } from './sentry.config';

const getSanitizedRequestUrl = (url?: string) => {
  if (!url) {
    return undefined;
  }

  try {
    const parsedUrl = new URL(url, window.location.origin);

    return `${parsedUrl.origin}${parsedUrl.pathname}`;
  } catch {
    return url.split('?')[0];
  }
};

export const axiosConfig = () => {
  const axiosConfig = {
    baseURL: API_URL,
    withCredentials: true,
  };
  const axiosInstance = axios.create(axiosConfig);

  axiosInstance.interceptors.response.use(
    response => {
      return response;
    },
    error => {
      const { pathname } = window.location;
      const errorCode = error?.response?.data?.code;
      const loginPage = `${APP_BASENAME}/login`;
      const requestUrl = error?.config?.url;
      const requestMethod = error?.config?.method;
      const statusCode = error?.response?.status;
      const axiosCode = error?.code;
      const sanitizedRequestUrl = getSanitizedRequestUrl(requestUrl);
      const isCanceledRequest = axiosCode === 'ERR_CANCELED';
      const statusFamily =
        typeof statusCode === 'number' ? `${Math.floor(statusCode / 100)}xx` : 'unknown';
      const httpErrorPolicy = getHttpErrorPolicy({ statusCode, axiosCode });
      /**
       * metadata:
       * - feature: observability.http-auth-noise-filter
       * - purpose: evitar incidencias de Sentry por 401 esperables durante check/login de sesion
       */
      const isExpectedAuthUnauthorized =
        statusCode === 401 &&
        [
          AUTH_URLS.CHECK_USER_AUTH,
          AUTH_URLS.LOGIN_SESSION_ACRONEX,
          AUTH_URLS.LOGIN_TOKEN_ACRONEX,
        ].some(authUrl => sanitizedRequestUrl?.endsWith(authUrl));

      if (!isCanceledRequest && !isExpectedAuthUnauthorized) {
        // Registra fallos HTTP aunque sean manejados aguas arriba por catch() en UI/repositorios.
        captureHandledError(error, {
          feature: 'http-client',
          action: 'axios.response.error',
          level: httpErrorPolicy.level,
          fingerprint: [
            'axios-response-error',
            requestMethod || 'unknown-method',
            sanitizedRequestUrl || 'unknown-url',
            String(statusCode || axiosCode || 'unknown-status'),
          ],
          tags: {
            statusCode,
            statusFamily,
            method: requestMethod,
            axiosCode,
            errorCategory: httpErrorPolicy.category,
            errorExpected: httpErrorPolicy.expected,
          },
          extra: {
            url: sanitizedRequestUrl,
            backendCode: errorCode,
            responseMessage: error?.response?.data?.message,
            baseURL: error?.config?.baseURL,
          },
        });
      }

      if (errorCode === AuthErrors.NOT_AUTHENTICATED) {
        if (pathname !== loginPage) {
          window.location.pathname = loginPage;
        }
      }

      const customError: HttpError = {
        ...error,
        message: error.response?.data?.message,
        statusCode,
      };

      return Promise.reject(customError);
    },
  );

  return axiosInstance;
};
